import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { FunctionComponent } from "react";
import { UserAccessLevels } from "./enums";

export enum LoadingState {
  NotStarted = 'not-asked',
  Loading = 'loading',
  Done = 'done',
  Error = 'error',
}

export interface AlertMessage {
  id: string;
  message: string;
  type: "success" | "error" | "warning";
  timestamp: number;
  dismissible?: boolean;
  autoDismiss?: boolean;
  dismissAfter?: number; // milliseconds
}

export interface AlertState {
  messages: AlertMessage[];
  isProcessing: boolean;
}

export interface AutoShipmentItem {
  customerNumber: string;
  itemId: string;
  frequency: number;
  description: string;
  startDate: string;
  shipmentDate: string;
  refillPeriodID: number;
  refillPeriodDate: string;
  refillPeriodName: string;
}

export interface ChildCustomerDetails {
  customer_number: string;
  customerService: CustomerService[];
  doctor_name: string;
  email: string;
  name: string;
  phone: string;
  fax: string;
  city: string;
  state: string;
  street: string;
  street2: string;
  zip: string;
  is_taxable: boolean;
  is_bill_to_parent: boolean;
  customer_osha_hipaa_metadata?: CustomerOshaHipaaMetadata[];
  teamMembers: TeamMember[];
  corporate_parent: boolean;
}

export interface ChildPracticeMetadata {
  customer_number: string;
  inactive: boolean;
  parent_customer_number: number;
  corporate_parent: boolean;
  is_taxable: boolean;
  name: string;
  city: string;
  state: string;
  street: string;
  street2: string;
  zip: string;
  phone: string;
  fax: string;
  email: string;
  doctor_name: string;
  status: number;
  bio_compliance_message: string;
  bio_compliance_status: number;
  bio_message_short: string;
  bio_message_long: string;
  bio_remaining_tests: number;
  device_message_short: string;
  device_message_long: string;
  device_compliance_status: number;
  active_standalone_devices: number;
  kit_message_short: string;
  kit_message_long: string;
  kit_compliance_status: number;
  mwaste_message_short: string;
  mwaste_message_long: string;
  mwaste_compliance_status: number;
  amalgam_message_short: string;
  amalgam_message_long: string;
  amalgam_compliance_status: number;
  pharma_message_short: string;
  pharma_message_long: string;
  pharma_compliance_status: number;
  waterline_message_short: string;
  waterline_message_long: string;
  waterline_compliance_status: number;
  amalgam_status: number;
  amalgam_available_containers: number;
  amalgam_destroyed_containers: number;
  amalgam_last_destroyed_date: string;
  pharma_status: number;
  pharma_available_containers: number;
  pharma_processed_containers: number;
  pharma_last_processed_date: string;
  mwaste_status: number;
  mwaste_available_containers: number;
  mwaste_destroyed_containers: number;
  mwaste_last_destroyed_date: string;
  medication_status: number;
  last_connection_date: string;
  active_med_kits: number;
  active_device_kits: number;
  device_status: number;
  compliant_aed_devices: number;
  aed_devices: number;
  expired_aed_devices: number;
  backordered_devices: number;
  expired_devices: number;
  expired_refilling_devices: number;
  expiring_devices: number;
  backordered_meds: number;
  expired_meds: number;
  expiring_meds: number;
  bio_last_status: string;
  bio_last_test_date: string;
  bio_num_active_autoclaves: number;
  autoclave_total?: number;
  autoclave_outstanding?: number;
  autoclave_confirmed_count?: number;
  autoclave_deactivated_count?: number;
  ot_account: string;
  training_message_short: string;
  training_message_long: string;
  training_compliance_status: number;
  training_past_due_count: number;
  training_due_soon_count: number;
  training_completed_count: number;
  osha_hipaa_message_short: string;
  osha_hipaa_message_long: string;
  osha_hipaa_compliance_status: number;
  osha_compliance_status: ComplianceStatus | null;
  hipaa_compliance_status: ComplianceStatus | null;
  osha_hipaa_training_compliance_status: ComplianceStatus | null;
  percentage_complete: number | null;
}

export interface Compliance {
  category: string;
  compliance_code: string;
  compliance_status: ComplianceStatus;
  message_long: string;
  message_short: string;
  priority: number;
  label?: string;
}

export interface Customer {
  customer_number: string;
  corporate_parent: boolean;
  doctor_name: string;
  name: string;
  is_taxable: boolean;
  is_bill_to_parent: boolean;  
}

export interface CustomerAddress {
  street: string | null;
  street2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  email: string | null;
  phone: string | null;
  fax: string | null;
}

export interface CustomerDetails extends Customer, CustomerAddress {
  groups: Groups[];
  customerService: CustomerService[];
}

export interface CustomerMetadata {
  customer_number: string;
  inactive: boolean;
  parent_customer_number: number;
  name: string;
  city: string;
  state: string;
  street: string;
  zip: string;
  phone: string;
  email: string;
  doctor_name: string;
  corporate_parent: boolean;
  is_bill_to_parent: boolean;
  status: number;
  bio_compliance_message: string;
  bio_compliance_status: number;
  bio_message_short: string;
  bio_message_long: string;
  device_message_short: string;
  device_message_long: string;
  device_compliance_status: number;
  kit_message_short: string;
  kit_message_long: string;
  kit_compliance_status: number;
  mwaste_message_short: string;
  mwaste_message_long: string;
  mwaste_compliance_status: number;
  amalgam_message_short: string;
  amalgam_message_long: string;
  amalgam_compliance_status: number;
  pharma_message_short: string;
  pharma_message_long: string;
  pharma_compliance_status: number;
  waterline_message_short: string;
  waterline_message_long: string;
  waterline_compliance_status: number;
  amalgam_status: number;
  amalgam_available_containers: number;
  amalgam_destroyed_containers: number;
  amalgam_last_destroyed_date: string;
  pharma_status: number;
  pharma_available_containers: number;
  pharma_processed_containers: number;
  pharma_last_processed_date: string;
  mwaste_status: number;
  mwaste_available_containers: number;
  mwaste_destroyed_containers: number;
  mwaste_last_destroyed_date: string;
  medication_status: number;
  last_connection_date: string;
  active_med_kits: number;
  active_device_kits: number;
  device_status: number;
  compliant_aed_devices: number;
  aed_devices: number;
  expired_aed_devices: number;
  backordered_devices: number;
  expired_devices: number;
  expired_refilling_devices: number;
  expiring_devices: number;
  active_standalone_devices: number;
  backordered_meds: number;
  expired_meds: number;
  expiring_meds: number;
  bio_last_status: string;
  bio_last_test_date: string;
  bio_num_active_autoclaves: number;
  bio_remaining_tests: number;
  ot_account: string;
  training_message_short: string;
  training_message_long: string;
  training_compliance_status: number;
  training_past_due_count: number;
  training_due_soon_count: number;
  training_completed_count: number;
  osha_hipaa_message_short: string;
  osha_hipaa_message_long: string;
  osha_hipaa_compliance_status: number;
  credit_hold: boolean;
  autoclave_outstanding?: number;
  waterline_compliance_interval: string;
}

export interface CustomerOshaHipaaMetadata {
  id: number;
  compliance_code: string;
  customer_number: string;
  hipaa_compliance_status: ComplianceStatus;
  osha_compliance_status: ComplianceStatus;
  training_compliance_status: ComplianceStatus;
  percentage_complete: number;
  updated_date: string;
  compliance: Compliance;
  year: number;
}

export interface CustomerService {
  isActive: boolean;
  serviceType: string;
}

export interface CustomerTraining {
  activation_date: string;
  created_date: string;
  customer_number: string;
  expiration_date: string;
  id: number;
  invoice_number: string;
  is_active: boolean;
  training: Training;
  training_id: number;
  updated_date: string;
}

export interface CustomerTrainingMetadata {
  compliance?: Compliance;
  customer_number: string;
  due_soon_count: number;
  id: number;
  past_due_count: string;
  training_compliance_message: string;
  training_compliance_status: number;
}

export interface Group {
  user_id: string;
  group_id: number;
  name: GroupName;
}

export interface Groups {
  group_id: number;
  name: string;
  customer_number: string;
  members: Members[];
}

export interface GroupName {
  group_id: number;
  name: string;
  customer_number: string;
}

export interface DevicesComplianceCounts {
  actionRequired: number;
  attentionNeeded: number;
  upToDate: number;
  inactive: number;
  activeCount: number;
  backorderedCount: number;
  expiringCount: number;
  expiredCount: number;
  upToDateAeds: number;
  upToDateDeviceKits: number;
  actionRequiredPercentage: number;
  attentionNeededPercentage: number;
  upToDatePercentage: number;
  inactivePercentage: number;
}

export interface MedicationsComplianceCounts {
  actionRequired: number;
  attentionNeeded: number;
  upToDate: number;
  inactive: number;
  activeCount: number;
  backorderedCount: number;
  expiringCount: number;
  expiredCount: number;
  actionRequiredPercentage: number;
  attentionNeededPercentage: number;
  upToDatePercentage: number;
  inactivePercentage: number;
}

export interface Members {
  group_id: number;
  customer_number: string;
}

export interface RefItem {
  category?: string;
  images?: string;
  is_backordered: boolean;
  is_controlled_substance: boolean;
  is_discontinued: boolean;
  is_refillable: boolean;
  is_sds: boolean;
  item_number: string;
  manufacturer_name?: string;
  name: string;
  price: number;
  subcategory?: string;
  type?: string;
}

export interface TbrItem {
  ItemNumber: string;
  RefillType: string;
}

export interface TeamMember {
  key_: number;
  user_id: string;
  customer_number: string;
  loc_id: number;
  firstname?: string;
  middlename?: string;
  lastname?: string;
  email: string;
  access_level: string;
  account_active: boolean;
  cellphone?: string;
  position?: string;
  login_user_id: string;
  last_connection_date?: string;
  verified: boolean;
  email_verified: boolean;
  email_bio_pass: boolean;
  email_bio_failinvalid: boolean;
  email_waterline_pass: boolean;
  is_notification_team_member_editable: boolean;
  email_bio_failed: boolean;
  email_bio_passed: boolean;
  email_bio_child_failed: boolean;
  email_waterline_failed: boolean;
  email_waterline_passed: boolean;
  email_waterline_child_failed: boolean;
  groups?: Group[];
}

export interface TeamMemberAvailableCustomer {
  accessLevel: string;
  customerNumber: string;
  name: string;
}

export interface Training {
  active_period: number;
  ce_credits: number;
  course_id: number;
  created_date: string;
  credit_eligible: boolean;
  days_to_complete: number;
  id: number;
  is_active: boolean;
  item_number: string;
  length: string;
  name: string;
  ref_item?: RefItem;
  userTrainings?: UserTraining[];
  year: number;
}

export interface TrainingInvoiceItem {
  customer_number: string;
  invoice_item_id: number;
  sku: string;
  year: number;
}

export interface SubscriptionItem {
  customerNumber: string;
  frequency: number;
  hold: boolean;
  itemId: string;
  location: string;
  quantity: number;
  startDate: string;
  userName: string | null;
}

export interface UserTraining {
  completion_date: string;
  compliance_status: number;
  course_id: string;
  created_date: string;
  customer_number: string;
  deleted_date: string;
  due_date: string;
  enrollment_date: string;
  id: number;
  is_active: boolean;
  lms_user_id: string;
  start_date: string;
  updated_date: string;
  user_id: string;
  teamMember?: TeamMember;
}

export interface UserTrainingMetadata {
  compliance: Compliance;
  completed_count: number;
  created_date: string;
  customer_number: string;
  due_soon_count: number;
  overall_compliance_status: string;
  past_due_count: number;
  updated_date: string;
  user_id: string;
}

export interface WasteManagementComplianceCounts {
  actionRequired: number;
  attentionNeeded: number;
  upToDate: number;
  inactive: number;
  inUse: number;
  processed: number;
  actionRequiredPercentage: number;
  attentionNeededPercentage: number;
  upToDatePercentage: number;
  inactivePercentage: number;
}

export enum ComplianceStatus {
  Complete = 1,
  Ok = 1,
  Passed = 1,
  InProgress = 2,
  Expiring = 2,
  RetestPassed = 2,
  Due = 2,
  NotStarted = 3,
  Expired = 3,
  Failed = 3,
  PastDue = 3,
  NotApplicable = 4,
  Inactive = 4,
  Unassigned = 4,
  green = 1,
  yellow = 2,
  red = 3,
  gray = 4,  
}

export interface MedicationsMetadata {
  backordered_meds: number;
  created_date: Date;
  customer_number: string;
  expired_meds: number;
  expiring_meds: number;
  expiring_under_two_weeks_meds: number;
  order_processing_meds: number;
  updated_date: Date;
}

declare global {
  interface Window {
    LC_API: {
      open_chat_window: () => void
    }
  }
}

export type ItemComplianceStatus = {
  compliance: { 
    status: number
  }
}

export type OpenGroupAlert = {
  open: boolean,
  type: string,
  message: string,
}

export type PickListOption = {
  customerNumber: string,
  value: string,
}

export type PickList = {
  available: PickListOption[],
  members: PickListOption[],
}

export type OpenGroupModal = {
  open: boolean,
  type: string,
}

export interface Biobox {
  active: boolean;
  autoclave_id: number | null;
  box_id: number;
  box_number: string;
  box_size: number;
  box_type: string;
  city: string;
  compliance_code: string;
  compliance_status: number;
  created_date: string;
  customer_number: string;
  doctor_name: string;
  exp_date: string;
  item_number: string;
  lot_number: string;
  message_long: string;
  message_short: string;
  name: string;
  parent_customer_number: string;
  priority: number;
  purchase_date: string;
  state: string;
  tests_run: number;
  updated_date: string;
}

export interface AutoReplenishmentItem {
  kit_serial_number: string;
  kit_model: string;
  po?: string;
  RefillPeriodName: string;
}

export interface AutoRenewalItem {
  item_number: number;
  name: string;
  quantity: number;
  category: string;
  frequency: number;
  nextShipment: string;
  readOnly: boolean;
  hold: boolean;
  startDate?: Date;
  itemId?: string;
}

export enum TestCompliance {
  passedTest = 'Test passed',
  failedTest = 'Failed test',
  passedRetest = 'Retest passed',
  failedRetest = 'Failed retest',
  missedTest = 'Missed test',
  inactive = 'Inactive',
  invalidTest = 'Invalid test',
}

export interface JobTitle {
  id?: number;
  job_title: string;
}

export interface DashBoardCard {
  title: string,
  icon: string | IconDefinition,
  link: string,
  content: FunctionComponent<{ link: string; }>,
}

export interface EmailDetails {
  notificationName: string
  firstname?: string
  lastname?: string
  userPhone?: string
  userEmail?: string
  contactOptions?: {
    phone: boolean
    email: boolean
  }
  message?: string
}

export interface UserNotificationGroup {
  category: string;
  group_id: string;
  user_id: string;
}

export interface User {
  access_level: UserAccessLevels;
  user_id: string | number;
  firstname?: string;
  middlename?: string;
  lastname?: string;
  email?: string;
  name?: string;
  timezone?: string | null;
  account_active?: boolean;
  login_user_id?: number;
  users_group_members?: string[];
  available_groups: Group[];
  is_corporate: boolean;
  position?: string;
  cellphone?: string;
  reminder_time?: string;
  user_notification_groups?: UserNotificationGroup[];
  email_bio_pass?: boolean;
  email_bio_failinvalid?: boolean;
  email_bio_failinvalid_child?: boolean;
  email_reminder?: boolean | number;
  email_waterline_pass?: boolean;
  is_notification_team_member_editable?: boolean;
  email_bio_failed?: boolean;
  email_bio_passed?: boolean;
  email_bio_child_failed?: boolean;
  email_waterline_failed?: boolean;
  email_waterline_passed?: boolean;
  email_waterline_child_failed?: boolean;
  available_customers?: CustomerDetails[];
  default_customer_number?: string;
  key_?: number;
  customer_number?: string;
}
